import './styles/graphic.scss';
import statements from './data/data.json';

const TAG_COLORS = {
  'Anti-Ukraine': '#EA9696',
  'Pro-Russia': '#EA9696',
  Neutral: '#E5B973',
  'Anti-Russia': '#93B5D6',
  'Pro-Ukraine': '#93B5D6',
};

const MONTHS = [
  'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
  'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
];

function getPosition(code) {
  if (code === 'Anti-Ukraine' || code === 'Pro-Russia') return 'right';
  if (code === 'Anti-Russia' || code === 'Pro-Ukraine') return 'left';
  return 'center';
}

function appendImage(wrapper, stmt, tagColor) {
  if (!stmt.Image) return;
  const isTruthSocial = stmt.Image === 'truth-social.png';

  if (isTruthSocial) {
    // Circular crop: aspect-ratio keeps a perfect square regardless of image
    // dimensions; outline sits outside the clipping region so it stays visible.
    const circle = wrapper.append('div')
      .style('width', '65%')
      .style('aspect-ratio', '1')
      .style('border-radius', '50%')
      .style('overflow', 'hidden')
      .style('outline', `4px solid ${tagColor}`)
      .style('outline-offset', '0px')
      .style('margin', '0 auto');

    circle.append('img')
      .attr('src', `images/${stmt.Image}`)
      .attr('alt', stmt.Caption || '')
      .style('width', '100%')
      .style('height', '100%')
      .style('display', 'block')
      .style('object-fit', 'cover');
  } else {
    wrapper.append('img')
      .attr('src', `images/${stmt.Image}`)
      .attr('alt', stmt.Caption || '')
      .style('width', '100%')
      .style('height', 'auto')
      .style('display', 'block');

    // Decorative color bar under non-circular images
    wrapper.append('div')
      .style('width', '100%')
      .style('height', '3px')
      .style('background-color', tagColor)
      .style('margin-bottom', '4px');
  }

  wrapper.append('div')
    .attr('class', 'stmt-caption')
    .style('text-align', isTruthSocial ? 'center' : null)
    .style('margin-top', isTruthSocial ? '14px' : null)
    .text(stmt.Caption || '');
}

function formatDate(raw) {
  let d;
  if (raw instanceof Date) {
    d = raw;
  } else if (typeof raw === 'number') {
    // Excel serial date: days since Jan 0 1900 (xlsx epoch)
    d = new Date(Math.round((raw - 25569) * 86400 * 1000));
  } else {
    // String "M/D/YYYY"
    const parts = String(raw).split('/');
    d = new Date(Date.UTC(
      parseInt(parts[2], 10),
      parseInt(parts[0], 10) - 1,
      parseInt(parts[1], 10),
    ));
  }
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

// ─── Connecting line ───────────────────────────────────────────────────────────
function drawConnectingLine(wrapperEl) {
  const wrapperRect = wrapperEl.getBoundingClientRect();
  const tagNodes = wrapperEl.querySelectorAll('.stmt-tag');
  const rowNodes = wrapperEl.querySelectorAll('.statement-row');
  const svgWidth = wrapperEl.offsetWidth;
  const svgHeight = wrapperEl.offsetHeight + 20;
  const svgNS = 'http://www.w3.org/2000/svg';

  const LEFT_RAIL = 8;
  const RIGHT_RAIL = svgWidth - 8;

  const badges = [];
  tagNodes.forEach((tagNode, i) => {
    const tagRect = tagNode.getBoundingClientRect();
    const rowRect = rowNodes[i].getBoundingClientRect();
    badges.push({
      code: statements[i].Code,
      pos: getPosition(statements[i].Code),
      tagY: (tagRect.top + tagRect.bottom) / 2 - wrapperRect.top,
      rowBottomY: rowRect.bottom - wrapperRect.top,
      tagLeft: tagRect.left - wrapperRect.left,
      tagRight: tagRect.right - wrapperRect.left,
    });
  });

  function segmentRails(pos1, pos2) {
    const naturalRail = p => (p === 'right' ? RIGHT_RAIL : p === 'left' ? LEFT_RAIL : null);
    const r1 = naturalRail(pos1);
    const r2 = naturalRail(pos2);
    // Same-side (right→right, left→left): r1===r2, elbow stays on same rail.
    // Cross-side (right→left, left→right): each badge uses its natural side so the
    // long crossing segment happens safely at gapY between rows.
    if (r1 !== null && r2 !== null) return [r1, r2];
    if (r1 !== null) return [r1, r1];
    if (r2 !== null) return [r2, r2];
    return [LEFT_RAIL, LEFT_RAIL];
  }

  function connectX(badge, rail) {
    return rail === RIGHT_RAIL ? badge.tagRight : badge.tagLeft;
  }

  const existing = wrapperEl.querySelector('.connecting-line-svg');
  if (existing) existing.remove();

  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('class', 'connecting-line-svg');
  svg.setAttribute('width', svgWidth);
  svg.setAttribute('height', svgHeight);
  svg.style.position = 'absolute';
  svg.style.top = '0';
  svg.style.left = '0';
  svg.style.pointerEvents = 'none';
  svg.style.overflow = 'visible';
  wrapperEl.appendChild(svg);

  // Per-segment gradient definitions
  const defs = document.createElementNS(svgNS, 'defs');
  svg.appendChild(defs);

  const makeGradient = (id, x1, y1, x2, y2, colorStart, colorEnd) => {
    const grad = document.createElementNS(svgNS, 'linearGradient');
    grad.setAttribute('id', id);
    grad.setAttribute('gradientUnits', 'userSpaceOnUse');
    grad.setAttribute('x1', x1);
    grad.setAttribute('y1', y1);
    grad.setAttribute('x2', x2);
    grad.setAttribute('y2', y2);
    const s1 = document.createElementNS(svgNS, 'stop');
    s1.setAttribute('offset', '0%');
    s1.setAttribute('stop-color', colorStart);
    grad.appendChild(s1);
    const s2 = document.createElementNS(svgNS, 'stop');
    s2.setAttribute('offset', '100%');
    s2.setAttribute('stop-color', colorEnd);
    grad.appendChild(s2);
    defs.appendChild(grad);
  };

  const makePath = (d, stroke) => {
    const p = document.createElementNS(svgNS, 'path');
    p.setAttribute('d', d);
    p.setAttribute('fill', 'none');
    p.setAttribute('stroke', stroke);
    p.setAttribute('stroke-width', '3');
    p.setAttribute('stroke-linecap', 'round');
    p.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(p);
    // Hide immediately on creation so the line never flashes before the
    // scroll animation initializes. getTotalLength() must run after append.
    const len = p.getTotalLength();
    p.style.strokeDasharray = len;
    p.style.strokeDashoffset = len;
  };

  for (let i = 0; i < badges.length - 1; i += 1) {
    const b1 = badges[i];
    const b2 = badges[i + 1];
    const [rail1, rail2] = segmentRails(b1.pos, b2.pos);
    const gapY = b1.rowBottomY + 14;

    const cx1 = connectX(b1, rail1);
    const cx2 = connectX(b2, rail2);

    const color1 = TAG_COLORS[b1.code] || '#E5B973';
    const color2 = TAG_COLORS[b2.code] || '#E5B973';

    const gradId = `seg-grad-${i}`;
    makeGradient(gradId, cx1, b1.tagY, cx2, b2.tagY, color1, color2);

    // Cross-side (right→left or left→right between two non-neutral badges):
    // exit source on its natural side, drop to gapY, cross at gapY directly
    // to the destination badge's same-direction edge (right side of a left badge,
    // or left side of a right badge), then a short rise to badge level.
    // This avoids any long horizontal arm at badge height.
    const isCross = b1.pos !== 'center' && b2.pos !== 'center' && b1.pos !== b2.pos;
    const pathD = (() => {
      if (isCross) {
        const crossCx2 = rail1 === RIGHT_RAIL ? b2.tagRight : b2.tagLeft;
        return [
          `M ${cx1}      ${b1.tagY}`,
          `L ${rail1}    ${b1.tagY}`,
          `L ${rail1}    ${b2.tagY}`,
          `L ${crossCx2} ${b2.tagY}`,
        ].join(' ');
      }
      return [
        `M ${cx1}   ${b1.tagY}`,
        `L ${rail1} ${b1.tagY}`,
        `L ${rail1} ${gapY}`,
        `L ${rail2} ${gapY}`,
        `L ${rail2} ${b2.tagY}`,
        `L ${cx2}   ${b2.tagY}`,
      ].join(' ');
    })();

    makePath(pathD, `url(#${gradId})`);
  }
}

// ─── Main graphic ──────────────────────────────────────────────────────────────
export default function graphic(props, container) {
  const { initialWidth } = props;
  const isMobile = initialWidth <= 500;

  container.html('');

  // Combined spectrum + legend: a gradient arrow across the top, with the three
  // colored squares and labels positioned at left / center / right beneath it,
  // mirroring the statements' horizontal positioning.
  const spectrum = container
    .append('div')
    .attr('class', 'statements-spectrum')
    .style('padding', isMobile ? '8px 10px 34px' : '10px 15px 44px')
    .style('max-width', isMobile ? null : '550px')
    .style('margin', isMobile ? null : '0 auto')
    .style('box-sizing', 'border-box');

  spectrum.append('div')
    .attr('class', 'spectrum-title')
    .style('text-align', 'center')
    .style('margin-bottom', '8px')
    .text('Trump statement position');

  // Row 1: gradient arrow
  const arrowRow = spectrum
    .append('div')
    .style('display', 'flex')
    .style('align-items', 'center')
    .style('margin-bottom', '4px');

  arrowRow.append('div')
    .style('width', '0')
    .style('height', '0')
    .style('flex-shrink', '0')
    .style('border-top', '5px solid transparent')
    .style('border-bottom', '5px solid transparent')
    .style('border-right', '8px solid #93B5D6');

  arrowRow.append('div')
    .style('flex', '1')
    .style('height', '3px')
    .style('background', 'linear-gradient(to right, #93B5D6, #E5B973, #EA9696)');

  arrowRow.append('div')
    .style('width', '0')
    .style('height', '0')
    .style('flex-shrink', '0')
    .style('border-left', '8px solid #EA9696')
    .style('border-top', '5px solid transparent')
    .style('border-bottom', '5px solid transparent');

  // Row 2: squares + labels aligned to left / center / right
  const legendItems = [
    {
      color: '#93B5D6', label: 'Pro-Ukraine/Anti-Russia', align: 'flex-start', textAlign: 'left',
    },
    {
      color: '#E5B973', label: 'Neutral', align: 'center', textAlign: 'center',
    },
    {
      color: '#EA9696', label: 'Pro-Russia/Anti-Ukraine', align: 'flex-end', textAlign: 'right',
    },
  ];

  const markers = spectrum
    .append('div')
    .style('display', 'flex')
    .style('justify-content', 'space-between')
    .style('align-items', 'flex-start');

  legendItems.forEach((item) => {
    const col = markers.append('div')
      .style('display', 'flex')
      .style('flex-direction', 'column')
      .style('align-items', item.align)
      .style('flex', '1 1 0')
      .style('min-width', '0');

    col.append('span')
      .attr('class', 'legend-label')
      .style('color', '#808080')
      .style('text-align', 'center')
      // One line on desktop (room to spare); break at the slash on mobile.
      .html(isMobile ? item.label.replace('/', '/<br>') : item.label);
  });

  const wrapper = container
    .append('div')
    .attr('class', 'statements-wrapper')
    .style('position', 'relative')
    .style('padding', isMobile ? '10px 10px' : '10px 15px')
    .style('box-sizing', 'border-box');

  statements.forEach((stmt, i) => {
    const pos = getPosition(stmt.Code);
    const tagColor = TAG_COLORS[stmt.Code] || '#E5B973';
    const text = (stmt.StatementShort && stmt.StatementShort.trim())
      ? stmt.StatementShort
      : stmt.Statement;
    const formattedDate = formatDate(stmt.Date);

    const imgScaleBase = isMobile ? 90 : 110;
    const imgScale = parseFloat(stmt.ImageScale) || 1;
    const imgColW = `${Math.round(imgScaleBase * imgScale)}px`;
    const contentMaxW = isMobile ? '62%' : '52%';

    const row = wrapper.append('div')
      .attr('class', `statement-row row-${pos}`)
      .attr('data-code', stmt.Code)
      .attr('data-index', i)
      .style('display', 'flex')
      .style('margin-bottom', isMobile ? '28px' : '36px')
      // Hidden from creation to avoid a flash of visible content before the
      // scroll animation initializes. Opacity-only (no transform) keeps the
      // statically-measured connecting line aligned.
      .style('opacity', '0')
      .style('transition', 'opacity 0.5s ease');

    if (pos === 'center') {
      // Neutral layout: image left + content right, centered as one unit.
      // Badge is inside the content column so it shares the same left edge
      // as the date and quote text.
      row
        .style('align-items', 'flex-start')
        .style('justify-content', 'flex-end');

      const imgWrapper = row.append('div')
        .attr('class', 'stmt-image-wrapper')
        .style('flex-shrink', '0')
        .style('width', imgColW)
        .style('padding-top', '32px')
        .style('padding-right', '14px')
        .style('padding-left', (pos === 'center' && imgScale === 1.5) ? '10px' : null);

      appendImage(imgWrapper, stmt, tagColor);

      const content = row.append('div')
        .attr('class', 'stmt-content')
        .style('max-width', contentMaxW)
        .style('min-width', '0');

      const tagRow = content.append('div')
        .attr('class', 'stmt-tag-row')
        .style('display', 'flex')
        .style('justify-content', 'flex-start')
        .style('width', '100%')
        .style('margin-bottom', '5px');

      tagRow.append('div')
        .attr('class', 'stmt-tag')
        .style('background-color', tagColor)
        .text(stmt.Code);

      content.append('div')
        .attr('class', 'stmt-date')
        .style('text-align', 'left')
        .text(formattedDate);

      content.append('div')
        .attr('class', 'stmt-text')
        .style('text-align', 'left')
        .style('padding-right', stmt.TextPaddingRight ? `${stmt.TextPaddingRight}px` : null)
        .text(`\u201C${text}\u201D`);
    } else {
      // Non-neutral: standard side-by-side layout
      const imageOnLeft = pos !== 'left';
      const rowJustify = pos === 'right' ? 'flex-end' : 'flex-start';
      const tagJustify = pos === 'right' ? 'flex-end' : 'flex-start';

      row
        .style('align-items', 'flex-start')
        .style('justify-content', rowJustify)
        .style('padding-right', pos === 'right' ? '20px' : '0')
        .style('padding-left', pos === 'left' ? '20px' : '0');

      const imgWrapper = row.append('div')
        .attr('class', 'stmt-image-wrapper')
        .style('flex-shrink', '0')
        .style('width', imgColW)
        .style('order', imageOnLeft ? '0' : '2')
        .style('padding-top', '32px')
        .style(imageOnLeft ? 'padding-right' : 'padding-left', '14px');

      appendImage(imgWrapper, stmt, tagColor);

      const content = row.append('div')
        .attr('class', 'stmt-content')
        .style('max-width', contentMaxW)
        .style('order', '1')
        .style('min-width', '0');

      const tagRow = content.append('div')
        .attr('class', 'stmt-tag-row')
        .style('display', 'flex')
        .style('justify-content', tagJustify)
        .style('width', '100%')
        .style('margin-bottom', '5px');

      tagRow.append('div')
        .attr('class', 'stmt-tag')
        .style('background-color', tagColor)
        .text(stmt.Code);

      const textAlign = pos === 'right' ? 'right' : 'left';

      content.append('div')
        .attr('class', 'stmt-date')
        .style('text-align', textAlign)
        .text(formattedDate);

      content.append('div')
        .attr('class', 'stmt-text')
        .style('text-align', textAlign)
        .style('padding-right', stmt.TextPaddingRight ? `${stmt.TextPaddingRight}px` : null)
        .text(`\u201C${text}\u201D`);
    }
  });

  // Wait for all images to load before measuring DOM positions for the line.
  // Without this, getBoundingClientRect() returns zero heights for unloaded
  // images, placing the connecting line in the wrong positions on first render.
  const imgs = Array.from(wrapper.node().querySelectorAll('img'));
  const imageLoads = imgs.map(
    img => new Promise((resolve) => {
      if (img.complete) {
        resolve();
      } else {
        img.addEventListener('load', resolve);
        img.addEventListener('error', resolve);
      }
    }),
  );

  Promise.all([document.fonts.ready, ...imageLoads]).then(() => {
    requestAnimationFrame(() => {
      drawConnectingLine(wrapper.node());
      initScrollAnimation(wrapper.node());
      if (window.pymChild) window.pymChild.sendHeight();
    });
  });
}

// ─── Scroll animation ──────────────────────────────────────────────────────────
function initScrollAnimation(wrapperEl) {
  const rows = Array.from(wrapperEl.querySelectorAll('.statement-row'));
  const paths = Array.from(wrapperEl.querySelectorAll('.connecting-line-svg path'));

  // Hide every row and prep each path for stroke-dashoffset animation
  // Rows are hidden (opacity 0) from creation and paths are hidden (dashoffset)
  // from creation. Here we only attach the draw transition — done after the
  // offset is already set so the line never animates out on load.
  paths.forEach((path) => {
    path.style.transition = 'stroke-dashoffset 0.55s ease';
  });

  let iframeTop = 0;
  let viewportHeight = window.innerHeight || 600;
  let isEmbedded = false;
  // Reveal a row once it is 25% into the viewport; retract it once it drops back
  // below 10%. Forward reveal happens immediately, but RETRACTION must persist
  // across several consecutive polls before it is applied (see checkVisibility).
  // The parent's reported viewport height / iframe position can spike between
  // polls — especially on mobile, where the URL bar collapsing and momentum /
  // rubber-band scrolling jolt the numbers — and a single bad frame would
  // otherwise push the current frontier row past the hide threshold, un-drawing
  // and then redrawing it: the "last line draws over and over + content flashes"
  // bug. Requiring the retract state to hold for CONFIRM_FRAMES ignores those
  // transient spikes while still reacting to a genuine scroll-up.
  const THRESHOLD = 0.25;
  const REVERSE_THRESHOLD = 0.1;
  const CONFIRM_FRAMES = 4; // ~400ms at the 100ms poll rate
  const DRAW_MS = 550; // base per-segment duration

  // Bidirectional line-drawing queue: paths draw one at a time, in order, so the
  // whole connecting line reads as a single continuous stroke. drawnCount steps
  // toward targetCount in whichever direction the user is scrolling.
  let drawnCount = 0; // segments currently drawn
  let targetCount = 0; // segments that should be drawn based on scroll position
  let isDrawing = false;
  const retractFrames = 0; // consecutive polls that have wanted a lower target

  const revealRow = (idx) => {
    if (rows[idx]) rows[idx].style.opacity = '1';
  };

  const hideRow = (idx) => {
    if (rows[idx]) rows[idx].style.opacity = '0';
  };

  const advanceDraw = () => {
    if (isDrawing || drawnCount === targetCount) return;
    isDrawing = true;

    // Catch-up: the further the draw is from the scroll target, the faster each
    // segment completes, so fast scrolling in either direction doesn't lag.
    const backlog = Math.abs(targetCount - drawnCount);
    const dur = backlog > 1
      ? Math.max(90, Math.round(DRAW_MS / backlog))
      : DRAW_MS;

    if (drawnCount < targetCount) {
      // Forward: draw segment `drawnCount`, reveal its destination row together.
      const path = paths[drawnCount];
      const len = path.getTotalLength();
      path.style.transition = `stroke-dashoffset ${dur}ms linear`;
      path.style.strokeDasharray = len;
      path.style.strokeDashoffset = '0';
      revealRow(drawnCount + 1);
      setTimeout(() => {
        drawnCount += 1;
        isDrawing = false;
        advanceDraw();
      }, dur);
    } else {
      // Reverse (scroll-up): un-draw the last segment and hide its row together.
      const idx = drawnCount - 1;
      const path = paths[idx];
      const len = path.getTotalLength();
      path.style.transition = `stroke-dashoffset ${dur}ms linear`;
      path.style.strokeDasharray = len;
      path.style.strokeDashoffset = len;
      hideRow(idx + 1);
      setTimeout(() => {
        drawnCount -= 1;
        isDrawing = false;
        advanceDraw();
      }, dur);
    }
  };

  const showAll = () => {
    rows.forEach((row) => {
      row.style.opacity = '1';
    });
    paths.forEach((path) => {
      path.style.strokeDashoffset = '0';
    });
    drawnCount = paths.length;
    targetCount = paths.length;
  };

  const checkVisibility = () => {
    // forwardMax: highest row that has scrolled past the reveal threshold.
    // reverseMax: highest row still above the hide threshold.
    let forwardMax = 0;
    let reverseMax = 0;
    rows.forEach((row, i) => {
      const { top } = row.getBoundingClientRect();
      const tpos = 1 - ((top + iframeTop) / viewportHeight);
      if (top <= 0 || tpos > THRESHOLD) forwardMax = Math.max(forwardMax, i);
      if (top <= 0 || tpos > REVERSE_THRESHOLD) reverseMax = Math.max(reverseMax, i);
    });

    // Row i is fed by segment i-1, so the target segment count equals the
    // highest revealed row index. Raise the target immediately when the user
    // scrolls down (forwardMax). Only LOWER it after the retract condition has
    // held for CONFIRM_FRAMES consecutive polls — a genuine scroll-up persists,
    // while transient viewport/position spikes (mobile URL bar, momentum) do
    // not, so the frontier line can no longer flash by un-drawing then redrawing.
    if (forwardMax > targetCount) {
      targetCount = forwardMax;
      retractFrames = 0;
    } else if (reverseMax < targetCount) {
      retractFrames += 1;
      if (retractFrames >= CONFIRM_FRAMES) {
        targetCount = reverseMax;
        retractFrames = 0;
      }
    } else {
      retractFrames = 0;
    }

    revealRow(0); // first row has no incoming segment; show it as soon as in view
    advanceDraw();
  };

  const { pymChild } = window;
  if (!pymChild) { showAll(); return; }

  // Poll parent for iframe position every 100 ms
  const pollInterval = setInterval(() => {
    pymChild.getParentPositionInfo();
  }, 100);

  pymChild.onMessage('viewport-iframe-position', (parentInfo) => {
    isEmbedded = true;
    const positions = parentInfo.split(' ').map(p => parseFloat(p));
    const vh = positions[1];
    const top = positions[2];
    // Ignore degenerate/garbage frames — a zero or NaN viewport height makes the
    // tpos math blow up and would momentarily hide everything, which then draws
    // back in on the next good frame (another source of endless redrawing).
    if (!Number.isFinite(vh) || vh <= 0 || !Number.isFinite(top)) return;
    viewportHeight = vh;
    iframeTop = top;
    checkVisibility();
  });

  // If not embedded after 600 ms (e.g. opened directly in browser), show all
  // and stop polling. If embedded, keep polling so scrolling keeps revealing rows.
  setTimeout(() => {
    if (!isEmbedded) {
      showAll();
      clearInterval(pollInterval);
    }
  }, 600);
}
